import type { CreateFormData } from "@/components/create/types";
import type {
  DesignBrief,
  DesignBriefClearedField,
  DressConcept,
} from "@/types/createAgent";
export {
  baseBriefFromForm,
  compileCurrentDesignSpec,
} from "@/lib/agent/briefForm";

export function cloneBrief(brief: DesignBrief): DesignBrief {
  return {
    ...brief,
    clearedFields: brief.clearedFields ? [...brief.clearedFields] : undefined,
    constraints: {
      ...brief.constraints,
      mustHave: [...brief.constraints.mustHave],
      avoid: [...brief.constraints.avoid],
      comfort: [...brief.constraints.comfort],
      budget: [...brief.constraints.budget],
      mobility: [...brief.constraints.mobility],
      bodyConcerns: [...(brief.constraints.bodyConcerns ?? [])],
      modesty: brief.constraints.modesty,
      coverage: [...(brief.constraints.coverage ?? [])],
      support: [...(brief.constraints.support ?? [])],
      opacity: [...(brief.constraints.opacity ?? [])],
      accessibility: [...(brief.constraints.accessibility ?? [])],
      cultural: [...(brief.constraints.cultural ?? [])],
      construction: [...(brief.constraints.construction ?? [])],
      referenceGuidance: [...(brief.constraints.referenceGuidance ?? [])],
      color: {
        primary: brief.constraints.color.primary,
        accents: [...(brief.constraints.color.accents ?? [])],
        preferred: [...brief.constraints.color.preferred],
        rejected: [...brief.constraints.color.rejected],
      },
    },
    event: brief.event
      ? {
          ...brief.event,
          wearerContext: [...(brief.event.wearerContext ?? [])],
        }
      : undefined,
    rejectedIdeas: [...brief.rejectedIdeas],
    openQuestions: [...brief.openQuestions],
    front: {
      ...brief.front,
      sleeveAddons: [...brief.front.sleeveAddons],
      mood: [...brief.front.mood],
      details: [...brief.front.details],
    },
    back: {
      ...brief.back,
      details: [...brief.back.details],
    },
    references: {
      front: [...brief.references.front],
      back: [...brief.references.back],
      inspiration: [...brief.references.inspiration],
      roles: brief.references.roles?.map(role => ({ ...role })),
    },
    provenance: brief.provenance ? { ...brief.provenance } : undefined,
  };
}

export function projectBriefToFormData(
  brief: DesignBrief,
  activeConcept?: DressConcept
): Partial<CreateFormData> {
  const visual = activeConcept?.visualDecisions;
  const cleared = new Set(brief.clearedFields ?? []);
  const visualValue = (
    field: DesignBriefClearedField,
    conceptValue: string | undefined,
    briefValue: string | undefined
  ) => (cleared.has(field) ? undefined : conceptValue || briefValue);
  return {
    ...(cleared.has("prompt")
      ? { prompt: "" }
      : brief.front.prompt
        ? { prompt: brief.front.prompt }
        : {}),
    ...(cleared.has("occasion")
      ? { preset_type: "" }
      : brief.occasion
        ? { preset_type: brief.occasion }
        : {}),
    ...(cleared.has("modesty")
      ? { modestyPreference: "" as never }
      : brief.constraints.modesty
        ? { modestyPreference: brief.constraints.modesty }
        : {}),
    ...(cleared.has("primaryColor")
      ? { color: "", colorHex: "" }
      : visualValue("primaryColor", visual?.color, brief.front.color)
        ? {
            color: visualValue(
              "primaryColor",
              visual?.color,
              brief.front.color
            ),
          }
        : {}),
    ...(cleared.has("length")
      ? { length: "" }
      : visualValue("length", visual?.length, brief.front.length)
        ? { length: visualValue("length", visual?.length, brief.front.length) }
        : {}),
    ...(cleared.has("sleeve")
      ? { sleeve: "" }
      : visualValue("sleeve", visual?.sleeve, brief.front.sleeve)
        ? { sleeve: visualValue("sleeve", visual?.sleeve, brief.front.sleeve) }
        : {}),
    // A required brief list is authoritative even when empty. Emitting the
    // empty array prevents a pre-brief form snapshot from restoring a removed
    // sleeve detail.
    sleeveAddons: brief.front.sleeveAddons,
    ...(cleared.has("neckline")
      ? { neckline: "" }
      : visualValue("neckline", visual?.neckline, brief.front.neckline)
        ? {
            neckline: visualValue(
              "neckline",
              visual?.neckline,
              brief.front.neckline
            ),
          }
        : {}),
    ...(cleared.has("waistline")
      ? { waistline: "" }
      : visualValue("waistline", visual?.waistline, brief.front.waistline)
        ? {
            waistline: visualValue(
              "waistline",
              visual?.waistline,
              brief.front.waistline
            ),
          }
        : {}),
    ...(cleared.has("silhouette")
      ? { silhouette: "" }
      : visualValue("silhouette", visual?.silhouette, brief.front.silhouette)
        ? {
            silhouette: visualValue(
              "silhouette",
              visual?.silhouette,
              brief.front.silhouette
            ),
          }
        : {}),
    ...(cleared.has("fabric")
      ? { fabric: "" }
      : visualValue("fabric", visual?.fabric, brief.front.fabric)
        ? { fabric: visualValue("fabric", visual?.fabric, brief.front.fabric) }
        : {}),
    ...(cleared.has("backPrompt")
      ? { backPrompt: "" }
      : visualValue("backPrompt", visual?.back, brief.back.prompt)
        ? {
            backPrompt: visualValue(
              "backPrompt",
              visual?.back,
              brief.back.prompt
            ),
          }
        : {}),
  };
}
